import { useState, useRef, useEffect } from "react";
import { galleryItems, galleryCategories } from "@/mocks/nailGallery";

export default function GallerySection() {
  const [activeCategory, setActiveCategory] = useState("すべて");
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const filtered =
    activeCategory === "すべて"
      ? galleryItems
      : galleryItems.filter((item) => item.category === activeCategory);

  return (
    <section id="gallery" className="py-20 lg:py-28 bg-[#FAFAF8]" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div
          className={`flex flex-col lg:flex-row lg:items-end justify-between mb-12 gap-6 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <div>
            <p className="font-['DM_Sans'] text-xs font-medium tracking-[0.3em] uppercase text-[#C9A87C] mb-3">
              Our Work
            </p>
            <h2 className="font-['Cormorant_Garamond'] text-5xl lg:text-6xl font-light text-[#1A1A1A] leading-tight">
              施術例ギャラリー
            </h2>
            <p className="font-['Noto_Sans_JP'] text-sm text-[#6B6B6B] mt-3">
              最新のトレンドネイルをご覧ください
            </p>
          </div>
          <a
            href="#booking"
            onClick={(e) => {
              e.preventDefault();
              const el = document.querySelector("#booking");
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }}
            className="hidden lg:flex items-center gap-2 border border-[#3D2E2A] text-[#3D2E2A] text-sm font-medium px-6 py-3 rounded-full hover:bg-[#3D2E2A] hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap font-['DM_Sans']"
          >
            このデザインで予約する
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>

        {/* Category Filter */}
        <div
          className={`flex flex-wrap gap-2 mb-10 transition-all duration-700 delay-100 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          {galleryCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap font-['DM_Sans'] tracking-wide ${
                activeCategory === cat
                  ? "bg-[#3D2E2A] text-white"
                  : "bg-[#F0EDE9] text-[#6B6B6B] hover:bg-[#E8E4DF]"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div
          className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 lg:gap-6 transition-all duration-700 delay-200 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
          }`}
        >
          {filtered.map((item, index) => (
            <div
              key={item.id}
              className="group bg-[#F5F3F0] rounded-[20px] overflow-hidden cursor-pointer hover:-translate-y-1 transition-transform duration-300"
              style={{ transitionDelay: `${index * 50}ms` }}
            >
              {/* Image */}
              <div className="relative h-[200px] md:h-[240px] overflow-hidden">
                <img
                  src={item.imageUrl}
                  alt={item.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-2.5 py-1 rounded-full">
                  <p className="font-['Noto_Sans_JP'] text-[10px] text-[#3D2E2A] font-medium">
                    {item.category}
                  </p>
                </div>
              </div>

              {/* Info */}
              <div className="px-4 py-4">
                <p className="font-['Noto_Sans_JP'] text-sm font-medium text-[#1A1A1A] mb-1">
                  {item.title}
                </p>
                <p className="font-['DM_Sans'] text-xs text-[#9E9189]">
                  {item.duration}
                </p>
                <div className="flex items-center justify-between mt-3">
                  <p className="font-['Cormorant_Garamond'] text-xl font-semibold text-[#3D2E2A]">
                    {item.price}
                  </p>
                  <button
                    onClick={() => {
                      const el = document.querySelector("#booking");
                      if (el) el.scrollIntoView({ behavior: "smooth" });
                    }}
                    className="w-8 h-8 flex items-center justify-center bg-[#3D2E2A] text-white rounded-full hover:bg-[#C9A87C] transition-colors duration-200 cursor-pointer"
                  >
                    <i className="ri-arrow-right-line text-xs"></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-14">
          <p className="font-['Noto_Sans_JP'] text-sm text-[#6B6B6B] mb-6">
            気に入ったデザインはございましたか？お気軽にご相談ください。
          </p>
          <button
            onClick={() => {
              const el = document.querySelector("#contact");
              if (el) el.scrollIntoView({ behavior: "smooth" });
            }}
            className="inline-flex items-center gap-2 bg-[#C9A87C] text-white text-sm font-medium px-10 py-3.5 rounded-full hover:bg-[#3D2E2A] transition-colors duration-300 cursor-pointer whitespace-nowrap font-['DM_Sans'] tracking-wider"
          >
            <i className="ri-message-3-line"></i>
            デザインについて相談する
          </button>
        </div>
      </div>
    </section>
  );
}
